import { ApolloError } from '@apollo/client';
import { GqlResponseError, isGqlResponseError } from 'utils/gql/errors/GqlResponseError';
import { AxiosError } from 'utils/gql/axios/AxiosClient';
import { logger } from './logger';
import { pathOr } from './pathOr';

interface InternalErrorAPIMessage {
  code: number;
  message: string;
}

export interface ErrorAPIMessage {
  friendlyMessage: string;
  internalError: InternalErrorAPIMessage | undefined;
  status: number | undefined;
  category: string | undefined;
}

export function generateMessage(
  friendlyMessage: string,
  error: GqlResponseError | AxiosError | InternalErrorAPIMessage | ApolloError,
  category?: string
) {
  const status =
    // eslint-disable-next-line no-nested-ternary
    (isGqlResponseError(error)
      ? error.status
      : isInternalErrorAPIMessage(error)
        ? error.code
        : (error as AxiosError).response?.status) ?? 200;
  const internalError = pathOr(
    { message: (error as ApolloError).graphQLErrors?.[0]?.message || error.message, code: status },
    ['response', 'errors', 0],
    error
  );
  logger.error(`API call failed with status: ${status}, message: ${error.message}`);
  logger.error(`Friendly message: ${friendlyMessage}, with category: ${category}`);

  const errorMessage: ErrorAPIMessage = {
    friendlyMessage,
    internalError,
    category,
    status
  };

  return errorMessage;
}

function isInternalErrorAPIMessage(error: unknown): error is InternalErrorAPIMessage {
  return !!error && typeof error === 'object' && 'code' in error;
}
